import { useCallback, useState } from 'react';

import {
  clearRoomPoll,
  closeRoomPoll,
  voteInRoomPoll,
} from 'mastodon/actions/rooms';
import type { ApiRoomPollJSON } from 'mastodon/api_types/rooms';
import { useAppDispatch } from 'mastodon/store';

interface Props {
  roomId: string;
  poll: ApiRoomPollJSON;
  /** Host or co-host: may close and clear. Everyone else may only vote. */
  canManage: boolean;
}

/**
 * The poll on the jumbotron (ticket 29).
 *
 * Two states, one card. Before you vote it is a list of buttons; the moment you
 * have voted — or the poll closes — the same list becomes bars with counts and
 * percentages. Results are live because the poll rides the room JSON and the
 * room already re-reads itself every 3s; there is no socket here.
 *
 * Deliberately shows results to a voter only AFTER they vote, so an early
 * landslide cannot steer the people who have not chosen yet.
 */
export const RoomPollCard: React.FC<Props> = ({ roomId, poll, canManage }) => {
  const dispatch = useAppDispatch();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);

  const hasVoted = poll.own_vote !== null;
  const showResults = hasVoted || poll.closed;

  const handleVote = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const raw = e.currentTarget.dataset.optionIndex;
      if (raw === undefined || busy) return;
      setBusy(true);
      setError(null);
      dispatch(voteInRoomPoll(roomId, poll.id, Number(raw)))
        .catch((err: unknown) => {
          const message = (err as { response?: { data?: { error?: string } } })
            .response?.data?.error;
          setError(message ?? 'Could not record that vote.');
        })
        .finally(() => {
          setBusy(false);
        });
    },
    [busy, dispatch, roomId, poll.id],
  );

  const handleClose = useCallback(() => {
    setBusy(true);
    setError(null);
    dispatch(closeRoomPoll(roomId, poll.id))
      .catch(() => {
        setError('Could not close the poll.');
      })
      .finally(() => {
        setBusy(false);
      });
  }, [dispatch, roomId, poll.id]);

  // Two-step, like the jumbotron's own Clear: a stray tap must not wipe a
  // result nobody has read yet.
  const handleClear = useCallback(() => {
    if (!confirmClear) {
      setConfirmClear(true);
      return;
    }
    setConfirmClear(false);
    setBusy(true);
    dispatch(clearRoomPoll(roomId, poll.id))
      .catch(() => {
        setError('Could not clear the poll.');
      })
      .finally(() => {
        setBusy(false);
      });
  }, [confirmClear, dispatch, roomId, poll.id]);

  return (
    <div
      className='room-poll'
      data-testid='room-poll'
      data-closed={poll.closed ? 'true' : 'false'}
    >
      <div className='room-poll__header'>
        <span className='room-poll__badge'>
          {poll.closed ? 'Poll closed' : 'Poll'}
        </span>
        {poll.author_name && (
          <span className='room-poll__author'>by {poll.author_name}</span>
        )}
      </div>

      <h3 className='room-poll__question'>{poll.question}</h3>

      <ul className='room-poll__options'>
        {poll.options.map((option) => {
          const mine = poll.own_vote === option.index;

          return (
            <li className='room-poll__option' key={option.index}>
              {showResults ? (
                <div
                  className='room-poll__result'
                  data-testid={`poll-result-${option.index}`}
                  data-mine={mine ? 'true' : 'false'}
                >
                  {/* The bar is decoration over text that already states the
                      numbers, so it is hidden from screen readers rather than
                      read out as a second, wordless copy of the same fact. */}
                  <span
                    className='room-poll__bar'
                    style={{ width: `${option.percent}%` }}
                    data-testid={`poll-bar-${option.index}`}
                    aria-hidden='true'
                  />
                  <span className='room-poll__result-text'>
                    <span className='room-poll__option-label'>
                      {option.text}
                      {mine && (
                        <span className='room-poll__mine' title='Your vote'>
                          {' '}
                          ✓
                        </span>
                      )}
                    </span>
                    <span className='room-poll__count'>
                      {option.percent}% ({option.votes}{' '}
                      {option.votes === 1 ? 'vote' : 'votes'})
                    </span>
                  </span>
                </div>
              ) : (
                <button
                  type='button'
                  className='room-poll__vote-btn'
                  data-option-index={option.index}
                  data-testid={`poll-vote-${option.index}`}
                  onClick={handleVote}
                  disabled={busy}
                >
                  {option.text}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      <div className='room-poll__footer'>
        <span className='room-poll__total' data-testid='poll-total'>
          {poll.total_votes} {poll.total_votes === 1 ? 'vote' : 'votes'}
          {!poll.closed && !hasVoted && ' so far — pick one'}
          {poll.closed && ' — final'}
        </span>

        {canManage && (
          <span className='room-poll__manage'>
            {!poll.closed && (
              <button
                type='button'
                className='room-poll__close-btn'
                onClick={handleClose}
                disabled={busy}
                data-testid='poll-close-btn'
              >
                Close poll
              </button>
            )}
            <button
              type='button'
              className='room-poll__clear-btn'
              onClick={handleClear}
              disabled={busy}
              data-testid='poll-clear-btn'
              aria-label={
                confirmClear ? 'Confirm clearing the poll' : 'Clear the poll'
              }
            >
              {confirmClear ? 'Clear — sure?' : 'Clear'}
            </button>
          </span>
        )}
      </div>

      {error && (
        <span className='room-poll__error' role='alert'>
          {error}
        </span>
      )}
    </div>
  );
};
